(() => {
  if (customElements.get('caha-fetes-hero')) return;
  const loadGSAP = (url) => {
    if (window.gsap) return Promise.resolve(window.gsap);
    if (!window.cahaFetesGSAP) window.cahaFetesGSAP = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url; script.async = true;
      script.onload = () => window.gsap ? resolve(window.gsap) : reject(new Error('GSAP unavailable'));
      script.onerror = reject;
      document.head.append(script);
    }).catch(error => { window.cahaFetesGSAP = null; throw error; });
    return window.cahaFetesGSAP;
  };
  class FetesHero extends HTMLElement {
    async connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      const generation = this.generation;
      this.abort = new AbortController();
      this.cards = [...this.querySelectorAll('.fc-card')];
      this.button = this.querySelector('.fc-pause');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.dataset.autoplay !== 'true';
      this.visible = true; this.hovered = false; this.focused = false; this.touchSelected = null; this.touchPending = false;
      if (!this.cards.length) return;
      try { this.gsap = await loadGSAP(this.dataset.gsap); } catch { return; }
      if (!this.isConnected || this.generation !== generation) return;
      const signal = this.abort.signal;
      this.querySelector('.fc-cta')?.addEventListener('click', event => {
        const link = event.currentTarget;
        const grid = document.getElementById('xmas_new') || document.querySelector('.shopify-section[id$="__products"]');
        if (grid && new URL(link.href).pathname === location.pathname) {
          event.preventDefault();
          grid.scrollIntoView({behavior:this.reduced.matches?'auto':'smooth',block:'start'});
        }
      }, {signal});
      this.cards.forEach((card, index) => {
        card.classList.add('fc-animated');
        card.addEventListener('pointerdown', event => { this.touchPending = event.pointerType === 'touch'; }, {signal});
        card.addEventListener('click', event => {
          if (!this.touchPending || event.detail === 0) return;
          this.touchPending = false;
          if (this.touchSelected !== index) {
            event.preventDefault(); this.touchSelected = index;
            this.open(index); this.sync();
          }
        }, {signal});
        card.addEventListener('pointerenter', event => {
          if (event.pointerType === 'touch') return;
          this.hovered = true; this.sync(); this.open(index);
        }, {signal});
        card.addEventListener('pointerleave', event => {
          if (event.pointerType === 'touch') return;
          this.hovered = false; this.sync();
        }, {signal});
        card.addEventListener('focus', () => { this.focused = true; this.sync(); if (!this.touchPending) this.open(index); }, {signal});
        card.addEventListener('blur', () => { this.focused = false; this.sync(); }, {signal});
      });
      this.button.addEventListener('click', () => { this.userPaused = !this.userPaused; this.touchSelected = null; this.sync(); }, {signal});
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') { this.touchSelected = null; this.open(-1); this.sync(); }
      }, {signal});
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target) || !event.target.closest('.fc-card')) {
          this.touchSelected = null; this.touchPending = false; this.sync();
        }
      }, {signal});
      document.addEventListener('visibilitychange', () => this.sync(), {signal});
      this.reduced.addEventListener('change', () => this.build(), {signal});
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting; this.sync();
      }, {threshold:0.05});
      this.observer.observe(this);
      this.build();
    }
    build() {
      this.context?.revert(); this.stopTimer(); this.active = -1; this.touchSelected = null;
      this.querySelector('.fc-snow').replaceChildren();
      this.button.hidden = this.reduced.matches;
      this.context = this.gsap.context(() => {
        this.gsap.set(this.querySelectorAll('.fc-door'), {rotationX:0,rotationY:0,opacity:1});
        if (this.reduced.matches) return;
        this.entrance = this.gsap.from(this.querySelectorAll('.fc-copy > *, .fc-card'), {
          y:16, duration:0.75, stagger:0.045, ease:'power3.out',clearProps:'opacity,transform'
        });
        this.snow = this.gsap.timeline({repeat:-1});
        const layer = this.querySelector('.fc-snow');
        for (let i=0, count=matchMedia('(max-width: 900px)').matches?14:32; i<count; i++) {
          const flake = document.createElement('i'); flake.className='fc-flake';
          flake.style.left=((i*47)%100)+'%'; layer.append(flake);
          this.snow.fromTo(flake,{y:-20,x:0,opacity:0},{y:()=>this.offsetHeight+35,x:(i%2?1:-1)*65,opacity:0.25+(i%3)*0.1,duration:7+i%5,ease:'none',repeat:-1},i*0.36);
        }
      },this);
      this.sync();
    }
    open(index) {
      if (index === this.active) return;
      this.active=index;
      this.dispatchEvent(new CustomEvent('caha:gift-open',{bubbles:true,detail:{index}}));
      this.context.add(() => {
        this.cards.forEach((card,i) => {
          const door=card.querySelector('.fc-door');
          const isOpen=i===index; card.classList.toggle('fc-selected',isOpen);
          this.gsap.to(door,{rotationY:this.reduced.matches?0:(isOpen&&i<5?Number(getComputedStyle(card).getPropertyValue('--angle')):0),rotationX:this.reduced.matches?0:(isOpen&&i>=5?-106:0),opacity:this.reduced.matches&&isOpen?0:1,duration:this.reduced.matches?0:0.85,ease:'power3.inOut',overwrite:true});
        });
      });
    }
    stopTimer() { clearTimeout(this.timer); this.timer=null; }
    sync() {
      if (!this.gsap) return;
      this.stopTimer();
      document.documentElement.classList.toggle('caha-motion-paused',this.userPaused);
      document.dispatchEvent(new CustomEvent('caha:motion-state',{detail:{paused:this.userPaused}}));
      const ambient = this.visible && !document.hidden && !this.userPaused && !this.reduced.matches;
      if(this.snow) this.snow.paused(!ambient);
      if(this.entrance) this.entrance.paused(!this.visible || document.hidden);
      if(!ambient) this.gsap.getTweensOf(this.querySelectorAll('.fc-door')).forEach(t=>t.progress(1));
      this.button.textContent=this.userPaused?this.dataset.playLabel:this.dataset.pauseLabel;
      this.button.setAttribute('aria-pressed',String(this.userPaused));
      if(ambient && !this.hovered && !this.focused && this.touchSelected === null) this.timer=setTimeout(()=>{
        this.open((this.active+1)%this.cards.length); this.sync();
      }, Number(this.dataset.interval)*1000);
    }
    disconnectedCallback() {
      this.generation=(this.generation||0)+1;
      this.stopTimer(); this.abort?.abort(); this.observer?.disconnect(); this.context?.revert();
      this.cards?.forEach(card=>card.classList.remove('fc-animated','fc-selected'));
    }
  }
  customElements.define('caha-fetes-hero',FetesHero);
})();
